/**
 * Core contracts shared by every module (BUILD-PLAN 5.1, 5.7).
 *
 * A module describes itself with a ModuleManifest. Web and API discover
 * manifests by globbing `src/modules/<id>/module.ts`, so there is no central
 * registry file to edit (BUILD-PLAN 5.1 rule 1). The manifest is plain data:
 * framework objects (React components, Fastify plugins) live next to it in the
 * app-specific module definition, never in this package.
 */
import { z } from 'zod';

/* ------------------------------------------------------------------------ */
/* Identifiers                                                               */
/* ------------------------------------------------------------------------ */

/** Module id: kebab-case, also the URL segment and API prefix (`/v1/<id>`). */
export const ModuleIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/, 'module id must be kebab-case');
export type ModuleId = z.infer<typeof ModuleIdSchema>;

/** Permission key `area.action`, for example `schedule.edit` (BUILD-PLAN 2.6). */
export const PermissionKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*\.[a-z][a-z0-9_]*$/, 'permission key must be area.action in snake_case');
export type PermissionKey = z.infer<typeof PermissionKeySchema>;

/** Marketplace entitlement key (BUILD-PLAN M23). Core modules use `null`. */
export const EntitlementKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9]*(?:[-_][a-z0-9]+)*$/, 'entitlement key must be kebab or snake case');
export type EntitlementKey = z.infer<typeof EntitlementKeySchema>;

/** SSE entity key published on change, for example `appointment` or `patient`. */
export const EntityKeySchema = z
  .string()
  .regex(/^[a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*$/, 'entity key must be dotted snake_case');
export type EntityKey = z.infer<typeof EntityKeySchema>;

/* ------------------------------------------------------------------------ */
/* Multi-office tenancy (ADR-0012)                                           */
/* ------------------------------------------------------------------------ */

/**
 * The office view a user has selected in the top-bar office selector.
 * `all` means every location the user may access, never every location in
 * the practice. Resolution against `user_location_access` is fail-closed; see
 * `resolveLocationScope` in @velora/domain.
 */
export const LocationScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('all') }),
  z.object({ kind: z.literal('location'), locationId: z.uuid() }),
]);
export type LocationScope = z.infer<typeof LocationScopeSchema>;

/**
 * How a route behaves with respect to the office selector.
 * - `location`: needs exactly one office (for example the schedule grid). In
 *   "All offices" mode the shell asks the user to pick one.
 * - `location-or-all`: works for one office or all accessible offices
 *   combined, and must show each office individually as well as the total
 *   (dashboards, analytics, worklists).
 * - `practice`: practice-wide, independent of the selector (practice settings,
 *   marketplace, user admin).
 */
export const OfficeScopeModeSchema = z.enum(['location', 'location-or-all', 'practice']);
export type OfficeScopeMode = z.infer<typeof OfficeScopeModeSchema>;

/* ------------------------------------------------------------------------ */
/* Manifest parts                                                            */
/* ------------------------------------------------------------------------ */

/** Side-nav groups in display order (BUILD-PLAN 3.4). */
export const NAV_GROUPS = [
  'dashboard',
  'schedule',
  'patients',
  'clinical',
  'revenue',
  'operations',
  'analytics',
  'marketplace',
  'meta',
  'settings',
] as const;
export const NavGroupSchema = z.enum(NAV_GROUPS);
export type NavGroup = z.infer<typeof NavGroupSchema>;

export const RouteSpecSchema = z.object({
  /** Unique within the module; referenced by nav items, commands, shortcuts. */
  id: z.string().regex(/^[a-z][a-zA-Z0-9-]*$/),
  /** Absolute app path, for example `/schedule` or `/patients/$patientId`. */
  path: z.string().regex(/^\/[a-z0-9/$_-]*$/i, 'route path must start with /'),
  title: z.string().min(1),
  permission: PermissionKeySchema.optional(),
  officeScope: OfficeScopeModeSchema,
  /** True when opening this route reads PHI (audit coverage, BUILD-PLAN 2.7). */
  readsPhi: z.boolean().default(false),
  /**
   * Marketplace entitlement of this route when it differs from the module's
   * (`null` for a free route inside a paid module). Omitted: the module's
   * `entitlementKey` applies.
   */
  entitlementKey: EntitlementKeySchema.nullable().optional(),
});
export type RouteSpec = z.infer<typeof RouteSpecSchema>;

export const NavItemSchema = z.object({
  label: z.string().min(1),
  /**
   * Skin terminology key (`@velora/contracts/skins/terms`, e.g.
   * `familyFile`): the active skin's wording replaces `label` ("Family File"
   * in Dentrix Familiar). `label` stays the Velora wording and the fallback.
   */
  labelKey: z
    .string()
    .regex(/^[a-z][a-zA-Z0-9]*$/)
    .optional(),
  group: NavGroupSchema,
  routeId: z.string(),
  /** Lucide icon name in kebab-case, for example `calendar-days`. Never an emoji. */
  icon: z.string().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'icon must be a Lucide icon name'),
  /** Sort order within the group; lower first. */
  order: z.number().int().default(100),
  permission: PermissionKeySchema.optional(),
});
export type NavItem = z.infer<typeof NavItemSchema>;

export const PaletteCommandSchema = z.object({
  /** Globally unique, namespaced by module: `<moduleId>.<name>`. */
  id: z.string().regex(/^[a-z][a-z0-9-]*\.[a-z][a-zA-Z0-9-]*$/),
  title: z.string().min(1),
  keywords: z.array(z.string()).default([]),
  /** Navigates to this route when chosen. Commands without a route are bound at runtime by the shell. */
  routeId: z.string().optional(),
  /** Search params for the route (`{ tab: 'analytics' }` opens Phones > Call analytics). */
  search: z.record(z.string(), z.string()).optional(),
  permission: PermissionKeySchema.optional(),
});
export type PaletteCommand = z.infer<typeof PaletteCommandSchema>;

export const ShortcutSchema = z.object({
  /** Key sequence in the shell's notation, for example `g s` or `mod+k`. */
  keys: z.string().min(1),
  description: z.string().min(1),
  scope: z.enum(['global', 'route']).default('global'),
  /** What the shortcut does: open a route or run a palette command. */
  routeId: z.string().optional(),
  commandId: z.string().optional(),
});
export type Shortcut = z.infer<typeof ShortcutSchema>;

/* ------------------------------------------------------------------------ */
/* ModuleManifest                                                            */
/* ------------------------------------------------------------------------ */

export const ModuleManifestSchema = z.object({
  id: ModuleIdSchema,
  title: z.string().min(1),
  /** Semver of the module's contract. */
  version: z.string().regex(/^\d+\.\d+\.\d+$/),
  /** Owning work package, for example `WP1-SCHEDULE` (see docs/MODULE-MAP.md). */
  owner: z.string().regex(/^WP\d+-[A-Z0-9-]+$/),
  /** Marketplace entitlement that unlocks this module; `null` for core modules. */
  entitlementKey: EntitlementKeySchema.nullable(),
  /** Every permission key this module checks, on server or client. */
  permissions: z.array(PermissionKeySchema).default([]),
  routes: z.array(RouteSpecSchema).default([]),
  nav: z.array(NavItemSchema).default([]),
  commands: z.array(PaletteCommandSchema).default([]),
  shortcuts: z.array(ShortcutSchema).default([]),
  /** Entity keys this module publishes on the SSE stream. */
  sseEntityKeys: z.array(EntityKeySchema).default([]),
  /** Named PHI read points, each must emit an audit_event (BUILD-PLAN 2.7). */
  phiReadPoints: z.array(z.string().min(1)).default([]),
});

/** A validated manifest with defaults applied. */
export type ModuleManifest = z.infer<typeof ModuleManifestSchema>;
/** What a module author writes; optional arrays may be omitted. */
export type ModuleManifestInput = z.input<typeof ModuleManifestSchema>;

/**
 * Validates and normalises a manifest. Call it in every `module.ts` so a
 * malformed manifest fails at import time, with the module id in the error.
 */
export function defineManifest(input: ModuleManifestInput): ModuleManifest {
  const result = ModuleManifestSchema.safeParse(input);
  if (!result.success) {
    const id = typeof input.id === 'string' ? input.id : '<unknown>';
    const details = result.error.issues
      .map((i) => `${i.path.length > 0 ? i.path.join('.') : '(root)'}: ${i.message}`)
      .join('; ');
    throw new Error(`Invalid module manifest "${id}": ${details}`);
  }
  return result.data;
}

/* ------------------------------------------------------------------------ */
/* Cross-module validation                                                   */
/* ------------------------------------------------------------------------ */

export interface ManifestIssue {
  moduleId: string;
  message: string;
}

/**
 * Checks a set of discovered manifests for conflicts that per-module schema
 * validation cannot see: duplicate ids, colliding route paths, dangling route
 * references, undeclared permissions and colliding global shortcuts.
 */
export function validateManifestSet(manifests: readonly ModuleManifest[]): ManifestIssue[] {
  const issues: ManifestIssue[] = [];
  const moduleIds = new Map<string, number>();
  const routePaths = new Map<string, string>();
  const commandIds = new Map<string, string>();
  const globalShortcuts = new Map<string, string>();

  for (const m of manifests) {
    const report = (message: string) => issues.push({ moduleId: m.id, message });

    moduleIds.set(m.id, (moduleIds.get(m.id) ?? 0) + 1);
    if (moduleIds.get(m.id) === 2) report(`duplicate module id "${m.id}"`);

    const declared = new Set(m.permissions);
    const requireDeclared = (perm: string | undefined, where: string) => {
      if (perm !== undefined && !declared.has(perm)) {
        report(`${where} uses permission "${perm}" which is not listed in permissions`);
      }
    };

    const routeIds = new Set<string>();
    for (const r of m.routes) {
      if (routeIds.has(r.id)) report(`duplicate route id "${r.id}"`);
      routeIds.add(r.id);
      const owner = routePaths.get(r.path);
      if (owner !== undefined) report(`route path "${r.path}" already registered by "${owner}"`);
      else routePaths.set(r.path, m.id);
      requireDeclared(r.permission, `route "${r.id}"`);
    }

    const requireRoute = (routeId: string | undefined, where: string) => {
      if (routeId !== undefined && !routeIds.has(routeId)) {
        report(`${where} references unknown route "${routeId}"`);
      }
    };

    for (const n of m.nav) {
      requireRoute(n.routeId, `nav item "${n.label}"`);
      requireDeclared(n.permission, `nav item "${n.label}"`);
    }

    const ownCommands = new Set<string>();
    for (const c of m.commands) {
      if (!c.id.startsWith(`${m.id}.`))
        report(`command "${c.id}" must be prefixed with "${m.id}."`);
      const owner = commandIds.get(c.id);
      if (owner !== undefined) report(`command id "${c.id}" already registered by "${owner}"`);
      else commandIds.set(c.id, m.id);
      ownCommands.add(c.id);
      requireRoute(c.routeId, `command "${c.id}"`);
      requireDeclared(c.permission, `command "${c.id}"`);
    }

    for (const s of m.shortcuts) {
      requireRoute(s.routeId, `shortcut "${s.keys}"`);
      if (s.commandId !== undefined && !ownCommands.has(s.commandId)) {
        report(`shortcut "${s.keys}" references unknown command "${s.commandId}"`);
      }
      if (s.routeId === undefined && s.commandId === undefined) {
        report(`shortcut "${s.keys}" must target a routeId or a commandId`);
      }
      if (s.scope === 'global') {
        const key = s.keys.trim().toLowerCase();
        const owner = globalShortcuts.get(key);
        if (owner !== undefined) report(`global shortcut "${s.keys}" already bound by "${owner}"`);
        else globalShortcuts.set(key, m.id);
      }
    }
  }
  return issues;
}

/** Throws with every issue listed when the manifest set is inconsistent. */
export function assertValidManifestSet(manifests: readonly ModuleManifest[]): void {
  const issues = validateManifestSet(manifests);
  if (issues.length > 0) {
    const lines = issues.map((i) => `  [${i.moduleId}] ${i.message}`).join('\n');
    throw new Error(`Module manifest conflicts:\n${lines}`);
  }
}

/* ------------------------------------------------------------------------ */
/* Background jobs                                                           */
/* ------------------------------------------------------------------------ */

/** Retry policy for a job. Money-moving jobs must set `limit: 0` (BUILD-PLAN M02). */
export interface JobRetryPolicy {
  /** Retries after the first attempt. Default 2. */
  limit?: number;
  /** Seconds before a retry. Default 30. */
  delaySeconds?: number;
  /** Exponential backoff on top of `delaySeconds`. Default true. */
  backoff?: boolean;
}

/** Facts about the running job, passed as the handler's second argument. */
export interface JobContext {
  /** Queue job id (stable across retries of the same job). */
  id: string;
  name: string;
  /** 0 on the first attempt. */
  retryCount: number;
  /**
   * Aborted when the run passes its `timeoutSeconds` (the worker also logs
   * it). Long handlers check it between batches and stop; a handler that
   * ignores it keeps running (JavaScript cannot be interrupted).
   */
  signal?: AbortSignal;
}

/**
 * A job a module contributes to the worker (`apps/api/src/modules/<id>/jobs.ts`
 * exports `jobs: JobDefinition[]`). The worker discovers these by glob and
 * binds them to pg-boss (WP0-API). Handlers that touch tenant data open a
 * tenant transaction with `withTenantTx` from `@velora/api/core`; payloads
 * carry ids only, never PHI.
 */
export interface JobDefinition<TData = unknown> {
  /** Queue name, namespaced `<moduleId>.<job>`. */
  name: string;
  /** Optional cron expression for scheduled jobs (worker time zone is UTC). */
  cron?: string;
  /** Payload for cron runs; validated by `schema` like any other payload. */
  cronData?: TData;
  /** Validates the payload before the handler runs (and before enqueue). */
  schema: z.ZodType<TData>;
  handler: (data: TData, ctx: JobContext) => Promise<void>;
  retry?: JobRetryPolicy;
  /** A run that exceeds this is failed and (if allowed) retried. Default 900. */
  timeoutSeconds?: number;
  /** pg-boss queue policy; `singleton` allows one queued or active job per key. */
  policy?: 'standard' | 'short' | 'singleton' | 'stately';
  /**
   * Worker-start catch-up for a daily or weekly cron whose slot was lost to a
   * worker restart (pg-boss never replays a missed cron):
   * - `after-hours` (default): heavy work; enqueued at once outside clinic
   *   hours, otherwise held until every office has closed.
   * - `any-time`: light work; enqueued at once.
   * - `false`: never caught up (money movement, patient messages).
   * Ignored for sub-daily crons, which simply fire again soon.
   */
  catchUp?: 'after-hours' | 'any-time' | false;
}

/* ------------------------------------------------------------------------ */
/* HTTP conventions (WP0-API)                                                */
/* ------------------------------------------------------------------------ */

/** Header carrying the office selector value (`all` or a location uuid). */
export const OFFICE_HEADER = 'x-velora-office';
/** Equivalent query parameter, used where headers cannot be set (EventSource). */
export const OFFICE_QUERY_PARAM = 'office';
/** Response header echoing the request id (also present in error bodies). */
export const REQUEST_ID_HEADER = 'x-request-id';

/** Stable machine-readable error codes. Clients branch on these, never on messages. */
export const ERROR_CODES = [
  'bad_request',
  'validation_failed',
  'unauthenticated',
  'forbidden',
  'office_required',
  'office_forbidden',
  'invalid_office',
  'impersonation_forbidden',
  'entitlement_required',
  'not_found',
  'conflict',
  'capability_unavailable',
  'payload_too_large',
  'rate_limited',
  'database_unavailable',
  'service_unavailable',
  'internal',
] as const;
export const ErrorCodeSchema = z.enum(ERROR_CODES);
export type ErrorCode = z.infer<typeof ErrorCodeSchema>;

/**
 * Every non-2xx JSON response has this shape. `details` is code-specific (for
 * example the issue list of `validation_failed` or the upsell data of
 * `entitlement_required`). Messages never contain PHI.
 */
export const ErrorEnvelopeSchema = z.object({
  error: z.object({
    code: z.string(),
    message: z.string(),
    requestId: z.string(),
    details: z.unknown().optional(),
  }),
});
export type ErrorEnvelope = z.infer<typeof ErrorEnvelopeSchema>;

/** `details` of a 402 `entitlement_required` (BUILD-PLAN M23). */
export const EntitlementRequiredDetailsSchema = z.object({
  feature: EntitlementKeySchema,
  upsellUrl: z.string(),
  trialAvailable: z.boolean(),
  /** True when the entitlement lapsed recently: reads still work, writes do not. */
  readOnly: z.boolean(),
  /**
   * Why it is locked (MK-13): `trial_ended`, `canceled`, `paused`, `past_due`,
   * `pending`, `read_only` or `not_entitled`.
   */
  reason: z.string().optional(),
});
export type EntitlementRequiredDetails = z.infer<typeof EntitlementRequiredDetailsSchema>;

/** One entry of the `details` array of a 400 `validation_failed`. */
export const ValidationIssueSchema = z.object({
  path: z.string(),
  message: z.string(),
});
export type ValidationIssue = z.infer<typeof ValidationIssueSchema>;

/* ------------------------------------------------------------------------ */
/* Live events (SSE /v1/events)                                              */
/* ------------------------------------------------------------------------ */

export const EVENTS_PATH = '/v1/events';

export const EntityChangeOpSchema = z.enum(['created', 'updated', 'deleted']);
export type EntityChangeOp = z.infer<typeof EntityChangeOpSchema>;

/**
 * Data of an `entity.changed` SSE event. Ids only, never PHI: the client
 * invalidates queries for `entityKey` (and `entityKey` + `id`) and refetches
 * through the normal, RLS-protected endpoints.
 */
export const EntityChangedSchema = z.object({
  entityKey: EntityKeySchema,
  id: z.string().min(1).max(200),
  op: EntityChangeOpSchema,
  /** Office of the row; `null` for practice-wide rows (for example a patient). */
  locationId: z.uuid().nullable(),
  version: z.number().int().nullable(),
  /** ISO time the change was published (server clock). */
  at: z.string(),
});
export type EntityChanged = z.infer<typeof EntityChangedSchema>;

/** SSE event names on `/v1/events`. */
export const SSE_EVENTS = {
  /** First event after connect, data `SseReady`. */
  ready: 'ready',
  /** Data `EntityChanged`. */
  entityChanged: 'entity.changed',
  /** The server could not replay from Last-Event-ID; invalidate everything. */
  resync: 'resync',
} as const;

export const SseReadySchema = z.object({
  bootId: z.string(),
  locationIds: z.array(z.uuid()),
});
export type SseReady = z.infer<typeof SseReadySchema>;

/* ------------------------------------------------------------------------ */
/* Audit (BUILD-PLAN 2.7)                                                    */
/* ------------------------------------------------------------------------ */

export const AuditActorKindSchema = z.enum(['user', 'system', 'automation', 'ai']);
export type AuditActorKind = z.infer<typeof AuditActorKindSchema>;

/** A stored, hash-chained audit event as the audit viewer reads it. */
export const AuditEventSchema = z.object({
  id: z.uuid(),
  practiceId: z.uuid(),
  /** Position in the practice's chain, from 1. */
  seq: z.number().int().positive(),
  occurredAt: z.string(),
  locationId: z.uuid().nullable(),
  actorKind: AuditActorKindSchema,
  actorUserId: z.uuid().nullable(),
  actorName: z.string(),
  impersonatorUserId: z.uuid().nullable(),
  impersonatorName: z.string().nullable(),
  /** `area.verb`, for example `patient.view` or `appointment.move`. */
  action: z.string(),
  entityType: z.string(),
  entityId: z.string().nullable(),
  phiRead: z.boolean(),
  ip: z.string().nullable(),
  userAgent: z.string().nullable(),
  requestId: z.string().nullable(),
  reason: z.string().nullable(),
  details: z.record(z.string(), z.unknown()),
  prevHash: z.string(),
  hash: z.string(),
});
export type AuditEvent = z.infer<typeof AuditEventSchema>;

/* ------------------------------------------------------------------------ */
/* Entitlements (ADR-0011)                                                   */
/* ------------------------------------------------------------------------ */

export const EntitlementSourceSchema = z.enum(['square', 'trial', 'grant', 'internal']);
export type EntitlementSource = z.infer<typeof EntitlementSourceSchema>;

/**
 * Effective state of a feature for one office:
 * - `active`: full use.
 * - `read_only`: lapsed less than 90 days ago; reads work, writes return 402.
 * - `none`: never entitled, revoked, or lapsed longer ago.
 */
export const EntitlementStateSchema = z.enum(['active', 'read_only', 'none']);
export type EntitlementState = z.infer<typeof EntitlementStateSchema>;

/* ------------------------------------------------------------------------ */
/* Files                                                                     */
/* ------------------------------------------------------------------------ */

/** A short-lived presigned object URL (BUILD-PLAN 2.3, 2.8: at most 5 minutes). */
export const SignedUrlSchema = z.object({
  url: z.string(),
  method: z.enum(['GET', 'PUT']),
  expiresAt: z.string(),
});
export type SignedUrl = z.infer<typeof SignedUrlSchema>;
